import { useState } from 'react'
import {
  App, Button, Card, Form, Input, Select, Space, Switch, Tag, Typography,
} from 'antd'
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import { maintenanceRequestsApi } from '@/features/maintenance/api'
import { machinesApi } from '@/features/masters/machines/api'
import { departmentsApi } from '@/features/masters/departments/api'
import { whatsappApi, openWhatsAppShare } from '@/features/notifications/api'
import DataTable from '@/shared/components/DataTable'
import FormDrawer from '@/shared/components/FormDrawer'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'
import { usePlantId } from '@/shared/hooks/usePlantId'
import type { MaintenanceRequest } from '@/shared/types/maintenance'
import MaintenanceStatusBadge from './components/MaintenanceStatusBadge'
import AuditTrailDrawer from './components/AuditTrailDrawer'
import WhatsAppShareButton from '@/shared/components/WhatsAppShareButton'

const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']
const PRODUCTION_IMPACTS = [
  { label: 'Full stop',     value: 'FULL_STOP' },
  { label: 'Partial stop',  value: 'PARTIAL_STOP' },
  { label: 'Quality issue', value: 'QUALITY' },
  { label: 'No impact',     value: 'NONE' },
]

/**
 * Breakdown Request Form — Production Supervisor / Maintenance Supervisor.
 *
 * ENTER ONCE → AUTO FETCH → AUTO PROMOTE.
 * ─────────────────────────────────────────
 * Spec §4. Selecting a machine auto-fills its department + criticality
 * → priority hint. Saving HIGH/CRITICAL or safety_risk shows an
 * immediate "Auto-promoted to WO" toast (driven by the API response's
 * `auto_promoted_to_wo` flag) so the reporter knows maintenance has been
 * woken up without needing to refresh.
 */
export default function BreakdownRequestPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { plantId } = usePlantId()
  const [form] = Form.useForm()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [statusFilter, setStatusFilter] = useState<string | undefined>()
  const [auditEntityId, setAuditEntityId] = useState<string | null>(null)

  const canCreate = usePermission('maintenance.create')

  // ── Lists ──────────────────────────────────────────────────────────────
  const { data: requests, isLoading } = useQuery({
    queryKey: ['maintenance-requests', plantId, statusFilter],
    enabled: !!plantId,
    queryFn: () => maintenanceRequestsApi.list({
      plant_id: plantId, status: statusFilter, limit: 200,
    }),
  })

  const { data: machinesData } = useQuery({
    queryKey: ['machines-for-maint', plantId],
    enabled: !!plantId,
    queryFn: () => machinesApi.list({ plant_id: plantId, is_active: true, page: 1, page_size: 500 }),
  })
  const machines = machinesData?.items ?? []

  const { data: deptsData } = useQuery({
    queryKey: ['departments-all'],
    queryFn: () => departmentsApi.list({ page: 1, page_size: 200 }),
  })
  const depts = deptsData?.items ?? []
  const deptName = (id: string | null) =>
    depts.find((d) => d.id === id)?.name ?? '—'
  const machineName = (id: string | null) =>
    machines.find((m) => m.id === id)?.name ?? '—'

  // ── Auto-fetch on machine select ───────────────────────────────────────
  const onMachineChange = (machineId: string) => {
    const m = machines.find((x) => x.id === machineId)
    if (!m) return
    // ENTER ONCE → AUTO FETCH:
    form.setFieldsValue({
      department_id: m.department_id,
      // Suggest priority from criticality. User can override.
      priority: form.getFieldValue('priority') ?? (
        m.criticality_rating === 'CRITICAL' ? 'HIGH'
          : m.criticality_rating === 'HIGH' ? 'MEDIUM'
            : 'LOW'
      ),
    })
  }

  // ── Save ───────────────────────────────────────────────────────────────
  const saveMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) => maintenanceRequestsApi.create({
      ...values,
      plant_id: plantId,
    }),
    onSuccess: async (req) => {
      qc.invalidateQueries({ queryKey: ['maintenance-requests'] })
      qc.invalidateQueries({ queryKey: ['work-orders'] })
      // The big motto kick — visible feedback when the system created a WO.
      if (req.auto_promoted_to_wo) {
        message.success({
          content: req.safety_risk
            ? `Request ${req.request_no} saved → EMERGENCY work order auto-created`
            : `Request ${req.request_no} saved → work order auto-created (priority ${req.priority})`,
          duration: 6,
        })
      } else {
        message.success(`Request ${req.request_no} created — pending supervisor review`)
      }
      setDrawerOpen(false)

      // Sprint 0078 — auto-open WhatsApp share for HIGH/CRITICAL/safety
      // so the supervisor can broadcast to the floor group in one tap.
      // For LOW/MEDIUM, no auto-share — supervisor uses the row's WhatsApp
      // button if they want.
      const shouldAutoShare =
        req.priority === 'HIGH' ||
        req.priority === 'CRITICAL' ||
        req.safety_risk === true
      if (shouldAutoShare) {
        try {
          const machName = machineName(req.machine_id) || 'Unknown machine'
          const dept = deptName(req.department_id) || 'Unknown dept'
          const link = await whatsappApi.generateLink({
            title:
              `🔴 BREAKDOWN — ${machName}` +
              (req.priority === 'CRITICAL' ? ' (CRITICAL)' : '') +
              (req.safety_risk ? ' ⚠️ SAFETY' : ''),
            body:
              `Request: ${req.request_no}\n` +
              `Department: ${dept}\n` +
              `Priority: ${req.priority}\n` +
              `Reported: ${dayjs(req.reported_datetime).format('DD MMM HH:mm')}\n` +
              `Issue: ${req.issue_description || '—'}`,
            entity_type: 'maintenance_requests',
            entity_id: req.id,
          })
          openWhatsAppShare(link.url)
        } catch {
          // Don't block UX on WhatsApp failure — the row's button is still there
        }
      }
    },
    onError: () => message.error('Failed to create request'),
  })

  const promoteMutation = useMutation({
    mutationFn: (id: string) => maintenanceRequestsApi.promote(id, { maintenance_type: 'BREAKDOWN' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['maintenance-requests'] })
      qc.invalidateQueries({ queryKey: ['work-orders'] })
      message.success('Promoted to work order')
    },
    onError: () => message.error('Promote failed'),
  })

  const onSubmit = () => form.validateFields().then((v) => saveMutation.mutate(v))

  // ── Columns ────────────────────────────────────────────────────────────
  const columns = [
    { title: 'Req No',  dataIndex: 'request_no', key: 'request_no', width: 130 },
    {
      title: 'Reported', dataIndex: 'reported_datetime', key: 'reported_datetime', width: 150,
      render: (v: string) => dayjs(v).format('DD MMM HH:mm'),
    },
    {
      title: 'Machine',  key: 'machine_id',
      render: (_: unknown, r: MaintenanceRequest) => machineName(r.machine_id),
    },
    {
      title: 'Department', key: 'department_id',
      render: (_: unknown, r: MaintenanceRequest) => deptName(r.department_id),
    },
    {
      title: 'Priority', dataIndex: 'priority', key: 'priority', width: 100,
      render: (v: string) => <MaintenanceStatusBadge status={v} />,
    },
    {
      title: 'Safety',   dataIndex: 'safety_risk', key: 'safety_risk', width: 80,
      render: (v: boolean) => v ? <Tag color="red">SAFETY</Tag> : null,
    },
    {
      title: 'Status',   dataIndex: 'status', key: 'status', width: 150,
      render: (v: string, r: MaintenanceRequest) => (
        <Space size={4} wrap>
          <MaintenanceStatusBadge status={v} />
          {r.auto_promoted_to_wo && <Tag color="purple">AUTO PROMOTED</Tag>}
        </Space>
      ),
    },
    { title: 'Issue', dataIndex: 'issue_description', key: 'issue', ellipsis: true },
    {
      title: 'Actions', key: 'actions', width: 360,
      render: (_: unknown, r: MaintenanceRequest) => (
        <Space size={4} wrap>
          {r.status === 'REPORTED' && (
            <Button size="small" type="link" onClick={() => promoteMutation.mutate(r.id)}>
              Promote to WO
            </Button>
          )}
          <Button size="small" type="link" onClick={() => setAuditEntityId(r.id)}>Audit</Button>
          {/* Sprint 0078 — share the breakdown to WhatsApp group */}
          <WhatsAppShareButton
            size="small"
            buttonText="WhatsApp"
            title={
              `🔴 BREAKDOWN — ${machineName(r.machine_id)}` +
              (r.priority === 'CRITICAL' ? ' (CRITICAL)' : '') +
              (r.safety_risk ? ' ⚠️ SAFETY' : '')
            }
            body={
              `Request: ${r.request_no}\n` +
              `Department: ${deptName(r.department_id)}\n` +
              `Priority: ${r.priority}\n` +
              `Reported: ${dayjs(r.reported_datetime).format('DD MMM HH:mm')}\n` +
              `Issue: ${r.issue_description || '—'}`
            }
            entityType="maintenance_requests"
            entityId={r.id}
          />
        </Space>
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        title="Breakdown Requests"
        subtitle="Report any machine issue once. HIGH/CRITICAL/safety auto-creates a work order."
        extra={canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => {
            form.resetFields()
            form.setFieldsValue({ priority: 'MEDIUM', machine_stopped: false, safety_risk: false })
            setDrawerOpen(true)
          }}>New Request</Button>
        )}
      />

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space>
          <Select
            allowClear placeholder="Filter by status" style={{ width: 200 }}
            options={['REPORTED', 'PROMOTED_TO_WO', 'CLOSED', 'CANCELLED'].map((s) => ({ label: s, value: s }))}
            onChange={setStatusFilter}
          />
          <Button icon={<ReloadOutlined />} onClick={() => qc.invalidateQueries({ queryKey: ['maintenance-requests'] })}>
            Refresh
          </Button>
          <Typography.Text type="secondary">
            Showing {requests?.length ?? 0} request(s)
          </Typography.Text>
        </Space>
      </Card>

      <DataTable rowKey="id" columns={columns} dataSource={requests} loading={isLoading} />

      <FormDrawer
        title="New Breakdown Request"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={onSubmit}
        loading={saveMutation.isPending}
        form={form}
        width={520}
      >
        <Form.Item
          name="machine_id" label="Machine" rules={[{ required: true, message: 'Select the affected machine' }]}>
          <Select
            showSearch
            placeholder="Select machine"
            options={machines.map((m) => ({
              label: `${m.machine_id_str} — ${m.name}`,
              value: m.id,
            }))}
            filterOption={(input, opt) =>
              (opt?.label as string).toLowerCase().includes(input.toLowerCase())
            }
            onChange={onMachineChange}
          />
        </Form.Item>
        <Form.Item name="department_id" label="Department (auto from machine)">
          <Select
            options={depts.map((d) => ({ label: d.name, value: d.id }))}
            placeholder="Auto-fetched"
            allowClear
          />
        </Form.Item>
        <Form.Item
          name="issue_description" label="Issue Description"
          rules={[{ required: true, message: 'Describe the issue' }, { min: 5, message: 'At least 5 characters' }]}>
          <Input.TextArea rows={3} placeholder="e.g. Spindle 14 making abnormal noise + smoke" />
        </Form.Item>
        {/*
          Sprint 0078 — "Machine fully stopped?" auto-sets production_impact.
          Backend uses production_impact == FULL_STOP to decide whether
          downtime starts from breakdown raise time (fully stopped) or
          from WO Start Work (running with issue).
        */}
        <Form.Item
          name="machine_stopped"
          label="Machine fully stopped?"
          valuePropName="checked"
          extra="Stopped → downtime counted from now. Running → counted from when maintenance starts work."
        >
          <Switch
            checkedChildren="Stopped"
            unCheckedChildren="Running"
            onChange={(checked) => {
              // Auto-sync production_impact based on toggle
              form.setFieldsValue({
                production_impact: checked ? 'FULL_STOP' : 'PARTIAL_STOP',
              })
            }}
          />
        </Form.Item>
        <Form.Item
          name="production_impact"
          label="Production Impact"
          extra="Auto-set from the toggle above; override here if needed."
        >
          <Select options={PRODUCTION_IMPACTS} placeholder="Optional" allowClear />
        </Form.Item>
        <Form.Item
          name="priority" label="Priority"
          rules={[{ required: true }]}
          extra="HIGH or CRITICAL will auto-create a work order on save.">
          <Select options={PRIORITIES.map((p) => ({ label: p, value: p }))} />
        </Form.Item>
        <Form.Item
          name="safety_risk" label="Safety risk?"
          valuePropName="checked"
          extra="If yes, an EMERGENCY work order is created regardless of priority.">
          <Switch checkedChildren="Yes" unCheckedChildren="No" />
        </Form.Item>
        <Form.Item name="remarks" label="Remarks">
          <Input.TextArea rows={2} />
        </Form.Item>
      </FormDrawer>

      <AuditTrailDrawer
        entityId={auditEntityId}
        title="Request Audit Trail"
        open={auditEntityId !== null}
        onClose={() => setAuditEntityId(null)}
      />
    </div>
  )
}

